import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, Building2, GraduationCap, Users, CreditCard,
  BarChart2, Wallet, QrCode, Settings, UtensilsCrossed, LogOut,
  X, ReceiptText, BookOpen, Package, Bus, DoorOpen, UserCheck,
  Fingerprint, ShieldCheck, ClipboardList,
  type LucideIcon,
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useAuth } from '@/hooks/useAuth';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import type { Role } from '@/types';

interface NavItem {
  to: string;
  label: string;
  Icon: LucideIcon;
  roles: Role[];
  end?: boolean;
  requires?: 'hasTransport' | 'hasAccessControl' | 'hasPreOrder';
}

const navItems: NavItem[] = [
  { to: '/dashboard',           label: 'Dashboard',       Icon: LayoutDashboard, roles: ['SUPER_ADMIN', 'INSTITUTION_ADMIN'] },
  { to: '/institutions',        label: 'Instituciones',   Icon: Building2,       roles: ['SUPER_ADMIN'] },
  { to: '/institution-admins', label: 'Administradores', Icon: ShieldCheck,     roles: ['SUPER_ADMIN', 'INSTITUTION_ADMIN'] },
  { to: '/students',            label: 'Alumnos',         Icon: GraduationCap,   roles: ['SUPER_ADMIN', 'INSTITUTION_ADMIN'] },
  { to: '/parents',             label: 'Tutores',         Icon: Users,           roles: ['SUPER_ADMIN', 'INSTITUTION_ADMIN'] },
  { to: '/staff',               label: 'Personal',        Icon: UserCheck,       roles: ['SUPER_ADMIN', 'INSTITUTION_ADMIN'] },
  { to: '/teacher-groups',      label: 'Maestras y Grupos', Icon: UserCheck,     roles: ['SUPER_ADMIN', 'INSTITUTION_ADMIN'], requires: 'hasPreOrder' },
  { to: '/cafeterias',          label: 'Cafeterías',      Icon: UtensilsCrossed, roles: ['SUPER_ADMIN', 'INSTITUTION_ADMIN'] },
  { to: '/pre-orders-admin',    label: 'Pre-orden',       Icon: ClipboardList,   roles: ['SUPER_ADMIN', 'INSTITUTION_ADMIN'], requires: 'hasPreOrder' },
  { to: '/transport',           label: 'Transporte',      Icon: Bus,             roles: ['SUPER_ADMIN', 'INSTITUTION_ADMIN'], requires: 'hasTransport' },
  { to: '/access',              label: 'Accesos',         Icon: DoorOpen,        roles: ['SUPER_ADMIN', 'INSTITUTION_ADMIN'], requires: 'hasAccessControl' },
  { to: '/biometric-devices',   label: 'Biometría',       Icon: Fingerprint,     roles: ['SUPER_ADMIN', 'INSTITUTION_ADMIN'] },
  { to: '/admin-payments',      label: 'Pagos',           Icon: CreditCard,      roles: ['SUPER_ADMIN', 'INSTITUTION_ADMIN', 'AUDITOR'] },
  { to: '/transactions',        label: 'Transacciones',   Icon: Wallet,          roles: ['SUPER_ADMIN', 'INSTITUTION_ADMIN', 'AUDITOR'] },
  { to: '/reports',             label: 'Reportes',        Icon: BarChart2,       roles: ['SUPER_ADMIN', 'INSTITUTION_ADMIN', 'AUDITOR'] },
  { to: '/my-payments',         label: 'Mis Recargas',    Icon: CreditCard,      roles: ['PARENT'] },
  { to: '/pre-orders',          label: 'Pre-orden',       Icon: ClipboardList,   roles: ['PARENT'] },
  { to: '/my-wallet',           label: 'Mi Saldo',        Icon: Wallet,          roles: ['STUDENT'] },
  { to: '/my-qr',               label: 'Mi Código QR',    Icon: QrCode,          roles: ['STUDENT'] },
  { to: '/cafeteria/dashboard',  label: 'Dashboard',      Icon: LayoutDashboard, roles: ['CAFETERIA'] },
  { to: '/cafeteria',            label: 'Cobro en Caja',  Icon: UtensilsCrossed, roles: ['CAFETERIA'], end: true },
  { to: '/cafeteria/menu',       label: 'Menú',           Icon: BookOpen,        roles: ['CAFETERIA'] },
  { to: '/cafeteria/inventario', label: 'Inventario',     Icon: Package,         roles: ['CAFETERIA'] },
  { to: '/cafeteria/ventas',     label: 'Bitácora',       Icon: ReceiptText,     roles: ['CAFETERIA'] },
  { to: '/cafeteria/pre-orders', label: 'Comandas',       Icon: ClipboardList,   roles: ['CAFETERIA'] },
  { to: '/transport/driver',    label: 'Validar Ruta',    Icon: Bus,             roles: ['DRIVER'] },
  { to: '/access/guard',        label: 'Control Acceso',  Icon: DoorOpen,        roles: ['GUARD'] },
  { to: '/my-groups',           label: 'Mis Grupos',      Icon: ClipboardList,   roles: ['TEACHER'] },
  { to: '/settings',            label: 'Configuración',   Icon: Settings,        roles: ['SUPER_ADMIN', 'INSTITUTION_ADMIN', 'AUDITOR', 'PARENT', 'STUDENT', 'CAFETERIA', 'STAFF', 'DRIVER', 'GUARD', 'TEACHER'] },
];

function initials(name: string) {
  return name.split(' ').slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('');
}

interface SidebarProps {
  open?: boolean
  onClose?: () => void
}

export function Sidebar({ open = false, onClose }: SidebarProps) {
  const user = useAuthStore((s) => s.user);
  const { logout } = useAuth();
  const role = user?.role;

  const visible = navItems.filter((i) => {
    if (!role || !i.roles.includes(role)) return false;
    if (i.requires && role !== 'SUPER_ADMIN' && user?.institution && user.institution[i.requires] === false) {
      return false;
    }
    return true;
  });

  const displayName = user?.student
    ? `${user.student.firstName} ${user.student.lastName}`
    : user?.parent
    ? `${user.parent.firstName} ${user.parent.lastName}`
    : user?.email?.split('@')[0] ?? '';

  return (
    <aside
      className={[
        'fixed inset-y-0 left-0 z-50 flex h-full w-64 flex-col bg-gray-950 border-r border-white/[0.04]',
        'transition-transform duration-200 ease-in-out',
        // Móvil: se traduce fuera/dentro de pantalla
        open ? 'translate-x-0' : '-translate-x-full',
        // Desktop: siempre visible, no fixed
        'md:relative md:translate-x-0 md:w-56',
      ].join(' ')}
    >
      {/* Logo + cerrar en móvil */}
      <div className="flex items-center justify-between px-5 py-5 mb-1">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-primary-700">
            <GraduationCap className="h-4 w-4 text-white" />
          </div>
          <span className="text-[13px] font-bold text-white tracking-tight">Cashless Escolar</span>
        </div>
        <button
          onClick={onClose}
          className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-600 hover:text-gray-300 hover:bg-white/10 transition-colors md:hidden"
          aria-label="Cerrar menú"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2.5 space-y-0.5 sidebar-scrollbar">
        {visible.map(({ to, label, Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={onClose}
            className={({ isActive }) =>
              `group flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13px] font-medium transition-all duration-100
              ${isActive
                ? 'bg-white/10 text-white'
                : 'text-gray-500 hover:bg-white/5 hover:text-gray-300'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon className={`h-4 w-4 shrink-0 transition-colors ${isActive ? 'text-primary-400' : 'text-gray-600 group-hover:text-gray-400'}`} />
                {label}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User */}
      <div className="px-2.5 py-3 border-t border-white/[0.06]">
        <div className="flex items-center gap-2 px-2 py-2 rounded-xl hover:bg-white/5 transition-colors">
          <div className="h-7 w-7 shrink-0 flex items-center justify-center rounded-lg bg-gradient-to-br from-primary-500 to-secondary-500 text-[10px] font-bold text-white">
            {initials(displayName) || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-semibold text-gray-300 truncate leading-none">{displayName}</p>
            <p className="text-[10px] text-gray-600 mt-0.5 leading-none truncate">{user?.email}</p>
          </div>
          <div className="shrink-0 text-gray-500 [&_button]:hover:text-gray-300 [&_button]:hover:bg-white/10 [&_span.absolute]:bg-red-500">
            <NotificationBell />
          </div>
          <button
            onClick={logout}
            title="Cerrar sesión"
            className="shrink-0 flex h-6 w-6 items-center justify-center rounded-lg text-gray-600 hover:text-gray-300 hover:bg-white/10 transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </aside>
  );
}
